<template>
  <!-- One column, top to bottom. Seven templates use it and differ in the header (beside the
       photo, centred, or a coloured band) and in how headings and entries are styled. -->
  <div :style="cfg.scale ? { fontSize: `${cfg.scale}em` } : null">
    <header :class="headerClass" :style="headerStyle">
      <ResumePhoto v-if="showPicture && cfg.header === 'center'" :url="pictureUrl" :shape="settings.picture_shape" :size="80" class="mx-auto mb-2" />

      <div class="min-w-0 flex-1">
        <h1 class="leading-tight" :class="cfg.name" :style="nameStyle">{{ basics.full_name || 'Your name' }}</h1>
        <p v-if="basics.headline" class="mt-0.5 text-[1.1em]" :style="cfg.header === 'band' ? { opacity: 0.92 } : null" :class="cfg.header === 'band' ? '' : cfg.headline">{{ basics.headline }}</p>
        <ResumeContacts v-if="contacts.length" :contacts="contacts" :center="cfg.header === 'center'" class="mt-1.5" :class="cfg.header === 'band' ? 'opacity-90' : 'text-gray-600'" />
      </div>

      <ResumePhoto v-if="showPicture && cfg.header !== 'center'" :url="pictureUrl" :shape="settings.picture_shape" :size="cfg.header === 'band' ? 92 : cfg.photo || 88" />
    </header>

    <div :style="{ display: 'flex', flexDirection: 'column', gap: `${gap}px`, marginTop: `${gap}px` }">
      <section v-for="section in sections" :key="section.id" class="resume-section">
        <h2 :class="cfg.heading" :style="headingStyle">{{ section.title }}</h2>
        <ResumeSection :section="section" :content="content" :accent="accent" :timeline="cfg.timeline === true" />
      </section>
    </div>

    <p v-if="!sections.length && !basics.full_name" class="mt-10 text-center text-gray-400">Your resume appears here as you type.</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ResumeContacts from './ResumeContacts.vue'
import ResumePhoto from './ResumePhoto.vue'
import ResumeSection from './ResumeSection.vue'
import { gapOf, readableOn } from './resumeLayout.js'
import { useResumeView } from './resumeView.js'

const props = defineProps({
  content: { type: Object, required: true },
  template: { type: String, default: 'modern' },
  pictureUrl: { type: String, default: null },
})

// header: row (name left, photo right), center, or band. accentName/accentHeading/accentRule: which parts take the accent.
const CONFIGS = {
  modern: {
    header: 'row', headerExtra: 'pb-3 border-b-2', name: 'text-[2.1em] font-bold', headline: 'font-medium text-gray-700',
    heading: 'mb-1.5 pb-0.5 text-[0.8em] font-bold uppercase tracking-[0.12em] border-b', accentName: true, accentHeading: true, accentRule: true,
  },
  classic: {
    header: 'center', headerExtra: 'pb-3 border-b border-gray-800', name: 'text-[2em] font-bold tracking-wide', headline: 'text-gray-600',
    heading: 'mb-1.5 pb-0.5 text-[0.85em] font-bold uppercase tracking-[0.1em] border-b border-gray-800', accentHeading: true,
  },
  minimal: {
    header: 'row', headerExtra: 'pb-1', name: 'text-[1.8em] font-semibold', headline: 'text-gray-600',
    heading: 'mb-1 text-[1em] font-bold', accentHeading: true,
  },
  harvard: {
    header: 'center', headerExtra: 'pb-2', name: 'text-[1.9em] font-bold tracking-[0.04em]', headline: 'text-gray-700', photo: 76,
    heading: 'mb-1.5 pb-0.5 text-[0.95em] font-bold uppercase tracking-[0.06em] border-b border-gray-900 text-gray-900', gapScale: 0.85,
  },
  compact: {
    header: 'row', headerExtra: 'pb-2 border-b border-gray-300', name: 'text-[1.7em] font-bold', headline: 'text-gray-700', photo: 72, scale: 0.94, gapScale: 0.7,
    heading: 'mb-1 pb-px text-[0.82em] font-bold uppercase tracking-[0.1em] border-b border-gray-300', accentHeading: true,
  },
  executive: {
    header: 'band', headerExtra: '', name: 'text-[2em] font-bold tracking-wide', headline: '',
    heading: 'mb-1.5 pl-2 text-[0.9em] font-bold uppercase tracking-[0.1em] border-l-4', accentHeading: true, accentBorder: true,
  },
  timeline: {
    header: 'row', headerExtra: 'pb-3', name: 'text-[2em] font-bold', headline: 'font-medium text-gray-700',
    heading: 'mb-2 text-[0.85em] font-bold uppercase tracking-[0.12em]', accentName: true, accentHeading: true, timeline: true,
  },
}

const { basics, settings, accent, contacts, sections, showPicture } = useResumeView(props)
const cfg = computed(() => CONFIGS[props.template] || CONFIGS.modern)

const gap = computed(() => Math.round(gapOf(settings.value) * (cfg.value.gapScale || 1)))

const headerClass = computed(() => {
  const kind = {
    row: 'flex items-center justify-between gap-5',
    center: 'text-center',
    band: 'flex items-center justify-between gap-5 px-6 py-5 rounded-lg',
  }[cfg.value.header]
  return [kind, cfg.value.headerExtra]
})

const headerStyle = computed(() => {
  if (cfg.value.header === 'band') return { backgroundColor: accent.value, color: readableOn(accent.value) }
  return cfg.value.accentRule ? { borderColor: accent.value } : null
})
const nameStyle = computed(() => (cfg.value.accentName ? { color: accent.value } : null))
const headingStyle = computed(() => {
  const style = {}
  if (cfg.value.accentHeading) style.color = accent.value
  if (cfg.value.accentRule || cfg.value.accentBorder) style.borderColor = accent.value
  return style
})
</script>
